'use client';

import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { SubscribeResponse } from '@/app/api/newsletter/subscribe/route';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export interface NewsletterSignupFormProps {
  locale: string;
  emailLabel: string;
  emailPlaceholder: string;
  nameLabel: string;
  namePlaceholder: string;
  submitLabel: string;
  submittingLabel: string;
  successHeading: string;
  successBody: string;
  genericError: string;
}

export function NewsletterSignupForm({
  locale,
  emailLabel,
  emailPlaceholder,
  nameLabel,
  namePlaceholder,
  submitLabel,
  submittingLabel,
  successHeading,
  successBody,
  genericError,
}: NewsletterSignupFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus('submitting');
    setErrorMessage(null);

    const trimmedName = name.trim();
    const body: { email: string; locale: string; name?: string } = {
      email: email.trim(),
      locale,
    };
    if (trimmedName) body.name = trimmedName;

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => ({}))) as
        | SubscribeResponse
        | Record<string, never>;

      if (res.ok && 'status' in json && json.status === 'accepted') {
        setStatus('success');
        return;
      }

      const apiError =
        'error' in json && typeof json.error === 'string' ? json.error : null;
      setErrorMessage(apiError ?? genericError);
      setStatus('error');
    } catch {
      setErrorMessage(genericError);
      setStatus('error');
    } finally {
      inFlightRef.current = false;
    }
  }

  if (status === 'success') {
    return (
      <div className="mt-6 text-center">
        <h3 className="text-xl font-semibold mb-2">{successHeading}</h3>
        <p className="text-muted-foreground">{successBody}</p>
      </div>
    );
  }

  const submitting = status === 'submitting';

  return (
    <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-start">
        <span className="text-sm font-medium">{emailLabel}</span>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={emailPlaceholder}
          disabled={submitting}
          autoComplete="email"
          className="bg-background"
        />
      </label>
      <label className="flex flex-col gap-1 text-start">
        <span className="text-sm font-medium">{nameLabel}</span>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={namePlaceholder}
          disabled={submitting}
          autoComplete="given-name"
          className="bg-background"
        />
      </label>
      {errorMessage && (
        <p role="alert" className="text-sm text-destructive text-start">
          {errorMessage}
        </p>
      )}
      <Button type="submit" disabled={submitting} size="lg">
        {submitting ? submittingLabel : submitLabel}
      </Button>
    </form>
  );
}
